import {
  Box,
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  CrudMode,
  ListContext,
  ListContextValue,
  Page,
  PageRoute,
  TabContext,
  Typography,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import {
  Application,
  ApplicationEditResource,
  ApplicationQuestion,
  ApplicationViewResource,
  Permission,
  PublicApplicationResponseDto,
  PublicApplicationViewResource,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { PublicApplicationQuestionPage } from "pages/public-forms/applications/PublicApplicationQuestionPage";
import { TransientQuestionResponse } from "pages/public-forms/common/TransientQuestionResponse";
import { useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import ApiService from "services/apiService";
import { ApplicationPageHeader } from "./ApplicationPageHeader";
import { ApplicationPageTabs } from "./ApplicationPageTabs";
import { ApplicationGeneralInfo } from "./General/ApplicationGeneralInfo";
import { ApplicationQuestions } from "./Questions/ApplicationQuestions";
import { ApplicationSections } from "./Sections/ApplicationSections";

const routeName = "application";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
}

class OptionalParams {
  tab?: ApplicationTab | undefined = undefined;
}

export const applicationPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Application",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export type ApplicationTab = "General" | "Questions" | "Sections";

const ROOT_PERMISSION: Permission = Permission.Contributor;

export const ApplicationPage = () => {
  const navigate = useNavigate();
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const [currentTab, setCurrentTab] = useState<ApplicationTab>(optionalParams.tab || "General");
  const [application, setApplication] = useState<Application>({} as Application);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [application] });
  const [refreshApplication, setRefreshApplication] = useState<Application>();
  const [copyApplication, setCopyApplication] = useState<number>();
  const [viewResource, setViewResource] = useState<ApplicationViewResource>({} as ApplicationViewResource);
  const [editResource, setEditResource] = useState<ApplicationEditResource>({} as ApplicationEditResource);
  const [printApplication, setPrintApplication] = useState<Application>();
  const [printViewResource, setPrintViewResource] = useState<PublicApplicationViewResource>();
  const [printActiveQuestions, setPrintActiveQuestions] = useState<ApplicationQuestion[]>([]);
  const [printTransientQuestionResponses, setPrintTransientQuestionResponses] = useState<TransientQuestionResponse[]>([]);
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const printContentRef = useRef(null);
  const reactToPrintFn = useReactToPrint({ contentRef: printContentRef });
  const appTenantName = useSelector((state: RootState) => state.app.tenantName);

  const { validationErrors, setValidationErrors } = useValidationErrors();

  // keeps track of which tabs data are loaded
  const [visitedTabs, setVisitedTabs] = useState<ApplicationTab[]>([]);

  useEffect(() => {
    fetchApplication(params.id);
    fetchViewResource(params.id);
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") fetchEditResource(params.id);
  }, [params.crudMode]);

  // load data from each tab as it's selected
  useEffect(() => {
    if (!visitedTabs.includes(currentTab)) {
      setVisitedTabs(
        produce((draft) => {
          draft.push(currentTab);
        })
      );
    }
  }, [currentTab]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${applicationPageRoute.getPath({ id: id, crudMode: crudMode })}`);

  const fetchApplication = (id: number) => {
    ApiService.applications.get(id).then((response) => {
      setApplication(response);
      setRefreshApplication(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = (id: number) => {
    ApiService.applications.getViewResource(id).then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = (id: number) => {
    ApiService.applications.getEditResource(id).then((response) => {
      setEditResource(response);
    });
  };

  const getApplicationQuestions = (application: Application): ApplicationQuestion[] =>
    application.applicationSections?.flatMap((section) => section.applicationQuestions || []) || [];

  const buildBlankTransientQuestionResponses = (application: Application): TransientQuestionResponse[] =>
    getApplicationQuestions(application).map((question) => ({
      questionId: question.id,
      writeIn: null,
      writeInDate: null,
      writeInDecimal: null,
      formAnswers: [],
      signatureFile: null,
      fileUpload: null,
    }));

  const fetchPrintApplication = async () => {
    if (!application.guid) return;

    const [applicationResponse, viewResourceResponse] = await Promise.all([
      ApiService.publicApplication.getApplication(application.guid, appTenantName),
      ApiService.publicApplication.getApplicationViewResource(application.guid, appTenantName),
    ]);

    const blankApplication = applicationResponse.application!;

    setPrintApplication(blankApplication);
    setPrintViewResource(viewResourceResponse);
    setPrintActiveQuestions(getApplicationQuestions(blankApplication));
    setPrintTransientQuestionResponses(buildBlankTransientQuestionResponses(blankApplication));
  };

  const prepPrint = async () => {
    if (!printApplication || printApplication.guid !== application.guid) {
      await fetchPrintApplication();
    }

    setIsPrinting(true);
  };

  const finishPrint = async () => {
    reactToPrintFn();
    setIsPrinting(false);
  };

  const updatePrintTransientResponse = (questionResponse: TransientQuestionResponse) => {
    setPrintTransientQuestionResponses(
      produce((draft) => {
        const draftQuestionResponseIndex = draft.findIndex((q) => q.questionId == questionResponse.questionId);

        draft[draftQuestionResponseIndex] = questionResponse;
      })
    );
  };

  const submitPrintResponse = (_applicationResponse: PublicApplicationResponseDto) => {};

  return (
    <TabContext value={currentTab}>
      <Page
        isForm
        header={
          <ApplicationPageHeader
            rootPermission={ROOT_PERMISSION}
            application={application}
            crudMode={params.crudMode}
            fetch={fetchApplication}
            setValidationErrors={setValidationErrors}
            unsavedChanges={unsavedChanges}
            reroute={reroute}
            printBlankApplication={application.id > 0 ? prepPrint : undefined}
          />
        }
        tabs={<ApplicationPageTabs viewResource={viewResource} setCurrentTab={setCurrentTab} />}
      >
        <Box hidden={currentTab !== "General"}>
          <ApplicationGeneralInfo
            application={application}
            setApplication={setApplication}
            setCopyApplication={setCopyApplication}
            viewResource={viewResource}
            editResource={editResource}
            crudMode={params.crudMode}
            validationErrors={validationErrors}
          />
        </Box>

        <Box hidden={currentTab !== "Sections"}>
          {application && (
            <ApplicationSections
              application={application}
              navigateEditMode={() => reroute(application.id, "edit")}
              copyApplication={copyApplication}
              refreshApplication={refreshApplication}
              setApplication={setApplication}
              viewResource={viewResource}
              editResource={editResource}
              crudMode={params.crudMode}
              validationErrors={validationErrors}
            />
          )}
        </Box>

        <Box hidden={currentTab !== "Questions"}>
          {application && (
            <ApplicationQuestions
              application={application}
              copyApplication={copyApplication}
              setApplication={setApplication}
              refreshApplication={refreshApplication}
              viewResource={viewResource}
              editResource={editResource}
              crudMode={params.crudMode}
              validationErrors={validationErrors}
              navigateEditMode={() => reroute(application.id, "edit")}
            />
          )}
        </Box>

        {printApplication && printViewResource && (
          <div ref={printContentRef} style={{ display: isPrinting ? "block" : "none" }}>
            <PublicApplicationQuestionPage
              application={printApplication}
              activeQuestions={printActiveQuestions}
              viewResource={printViewResource}
              transientQuestionResponses={printTransientQuestionResponses}
              validationErrors={{}}
              isViewMode={false}
              isReviewMode={false}
              isPrinting={isPrinting}
              showBlankPrintFields
              printCallBack={finishPrint}
              updateQuestionResponse={updatePrintTransientResponse}
              submitResponse={submitPrintResponse}
            />
            <Box position="relative" bottom={20} zIndex={100} textAlign="center">
              <Typography variant="caption">
                <>Copyright © 2006-{new Date().getFullYear()} Cobro Consulting. All Rights Reserved.</>
              </Typography>
            </Box>
          </div>
        )}
      </Page>
    </TabContext>
  );
};
